import React, { useState } from 'react';
import { useOS, WALLPAPERS } from '../OSContext';

const THEME_OPTIONS = [
	{
		id: 'v2',
		label: 'Game Mode',
		description: 'A playful, game-inspired take on the portfolio.',
		icon: 'fa-solid fa-gamepad',
	},
	{
		id: 'os',
		label: 'OS Mode',
		description: 'This macOS-style desktop experience.',
		icon: 'fa-solid fa-display',
	},
];

const NAV_ITEMS = [
	{ id: 'appearance', label: 'Appearance', icon: 'fa-solid fa-swatchbook' },
	{ id: 'theme', label: 'Theme', icon: 'fa-solid fa-desktop' },
];

const SettingsApp = () => {
	const { theme, setTheme, wallpaper, setWallpaper } = useOS();
	const [section, setSection] = useState('appearance');

	return (
		<div className="font-os flex h-full overflow-hidden bg-os-surface text-os-text">
			<div className="flex w-44 shrink-0 flex-col gap-0.5 border-r border-white/10 bg-os-surface2 p-2">
				{NAV_ITEMS.map(item => (
					<button
						key={item.id}
						onClick={() => setSection(item.id)}
						className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${
							section === item.id
								? 'bg-os-accent text-white'
								: 'text-os-textdim hover:bg-os-surface3 hover:text-os-text'
						}`}
					>
						<i className={`${item.icon} w-4 text-center`} />
						{item.label}
					</button>
				))}
			</div>

			<div className="flex-1 overflow-y-auto">
				{section === 'appearance' && (
					<div className="mx-auto max-w-lg px-8 py-8">
						<h1 className="text-lg font-semibold text-os-text">Appearance</h1>
						<p className="mt-1 text-sm text-os-textdim">Choose a desktop wallpaper.</p>

						<div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
							{WALLPAPERS.map(w => {
								const active = w.id === wallpaper;
								return (
									<button
										key={w.id}
										onClick={() => setWallpaper(w.id)}
										className="flex flex-col items-center gap-2"
									>
										<span
											style={{ backgroundImage: w.css }}
											className={`block h-16 w-full rounded-xl border shadow-[0_4px_14px_rgba(0,0,0,0.35)] transition-all ${
												active
													? 'border-os-accent ring-2 ring-os-accent ring-offset-2 ring-offset-os-surface'
													: 'border-white/10 hover:border-white/30'
											}`}
										/>
										<span
											className={`flex items-center gap-1 text-xs ${
												active ? 'text-os-accent' : 'text-os-textdim'
											}`}
										>
											{active && <i className="fa-solid fa-check text-[10px]" />}
											{w.label}
										</span>
									</button>
								);
							})}
						</div>
					</div>
				)}

				{section === 'theme' && (
					<div className="mx-auto max-w-lg px-8 py-8">
						<h1 className="text-lg font-semibold text-os-text">Theme</h1>
						<p className="mt-1 text-sm text-os-textdim">
							Switch how the whole portfolio site is presented.
						</p>

						<div className="mt-6 space-y-3">
							{THEME_OPTIONS.map(opt => {
								const active = theme === opt.id;
								return (
									<div
										key={opt.id}
										className={`flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
											active
												? 'border-os-accent/50 bg-os-accent/10'
												: 'border-white/10 bg-os-surface2'
										}`}
									>
										<div className="flex items-center gap-3">
											<div
												className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-base ${
													active ? 'bg-os-accent text-white' : 'bg-os-surface3 text-os-textdim'
												}`}
											>
												<i className={opt.icon} />
											</div>
											<div>
												<p className="text-sm font-medium text-os-text">{opt.label}</p>
												<p className="text-xs text-os-textdim">{opt.description}</p>
											</div>
										</div>

										{active ? (
											<span className="shrink-0 rounded-md bg-os-accent/20 px-3 py-1.5 text-xs font-medium text-os-accent">
												Active
											</span>
										) : (
											<button
												onClick={() => setTheme(opt.id)}
												className="shrink-0 rounded-md bg-os-surface3 px-3 py-1.5 text-xs font-medium text-os-text transition-colors hover:bg-white/20"
											>
												Switch
											</button>
										)}
									</div>
								);
							})}
						</div>

						<p className="mt-6 text-xs text-os-textdim">
							Switching themes changes the entire site's presentation, not just this window.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default SettingsApp;
